/* eslint-disable */
import { Button } from "antd";
import { MailOutlined, PrinterOutlined } from "@ant-design/icons";
import * as React from "react";
import { dateValidation } from "../helpers/dateValidation";
import { useStore } from "../../../stores/root-store-context";
import { observer } from "mobx-react-lite";
import Spin from '../../../../../../../../Components/Spinner/SectionSpinner';
import ResponsiveGrid from '../../../../../../../../Components/TableMui/ResponsiveGrid';

export interface AccountDetailProps {
    dataSource: AccountDetailItem[];
}

export type AccountDetailItem = {
    accountNumber: string,
    transactionDate: Date,
    transactionDateStr: string,
    postedDate: Date,
    postedDateStr: string,
    code: string,
    amoun: number,
    balance: number,
    checkNumber: string,
    memo: string
}

const columns = [
    { field: "transactionDateStr", label: "Transaction", flex: 1, minWidth: 120, render: (value) => dateValidation(value) },
    { field: "postedDateStr", label: "Posted", flex: 1, minWidth: 120, render: (value) => dateValidation(value) },
    { field: "code", label: "Code", flex: 0.8, minWidth: 90 },
    { field: "amount", label: "Amount", flex: 0.9, minWidth: 100, render: (value) => `$${Number(value).toFixed(2)}` },
    { field: "balance", label: "Balance", flex: 0.9, minWidth: 100, render: (value) => `$${Number(value).toFixed(2)}` },
    { field: "checkNumber", label: "Check #", flex: 0.8, minWidth: 90 },
    { field: "memo", label: "Memo", flex: 1.4, minWidth: 150 },
];

const PAGE_SIZE = 10;

// Displayed date columns are strings; sort them by their real date value.
const DATE_SORT_FIELDS: Record<string, string> = {
    transactionDateStr: "transactionDate",
    postedDateStr: "postedDate",
};

const sortValue = (row: any, field: string) => {
    if (DATE_SORT_FIELDS[field]) {
        const time = new Date(row?.[DATE_SORT_FIELDS[field]] ?? row?.[field]).getTime();
        return Number.isNaN(time) ? null : time;
    }
    return row?.[field];
};

const defaultCompare = (a: any, b: any): number => {
    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;
    if (typeof a === "number" && typeof b === "number") return a - b;
    return String(a).localeCompare(String(b), undefined, { numeric: true });
};

const AccountDetail: React.FC<AccountDetailProps> = observer(({ dataSource }) => {
    const { ricStore } = useStore();

    const rows = React.useMemo(
        () => (dataSource ?? []).map((el, index) => ({ ...el, key: index })),
        [dataSource]
    );

    const [page, setPage] = React.useState<number>(1);
    const [sortModel, setSortModel] = React.useState<{ field: string; sort: "asc" | "desc" }[]>([]);

    React.useEffect(() => {
        const maxPage = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
        if (page > maxPage) {
            setPage(maxPage);
        }
    }, [rows.length, page]);

    const sortedRows = React.useMemo(() => {
        if (!sortModel.length) return rows;
        const { field, sort } = sortModel[0];
        const dir = sort === "desc" ? -1 : 1;
        return [...rows].sort(
            (a, b) => dir * defaultCompare(sortValue(a, field), sortValue(b, field))
        );
    }, [rows, sortModel]);

    const pagedRows = React.useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return sortedRows.slice(start, start + PAGE_SIZE);
    }, [sortedRows, page]);

    const gridRef = React.useRef<HTMLDivElement>(null);
    const [visible, setVisible] = React.useState(true);

    React.useEffect(() => {
        const el = gridRef.current;
        if (!el) return;
        const observer = new ResizeObserver(() => setVisible(el.offsetWidth > 0));
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={gridRef} className="ric-pane">
            <div className="ric-toolbar">
                <div className="ric-toolbar__action">
                    <Spin className="section-spin" spinning={ricStore.isLoadAccountInfo}>
                        <Button
                            className="ric-pill-btn"
                            icon={<PrinterOutlined />}
                            onClick={() => {
                                ricStore.createNewPdfFinancials();
                            }}
                        >
                            Print Account Detail
                        </Button>
                    </Spin>
                </div>
            </div>
            <ResponsiveGrid
                key={visible ? "visible" : "hidden"}
                columns={columns}
                rows={pagedRows}
                getRowId={(row) => row.key}
                loading={ricStore.isLoadAccountInfo}
                emptyText="No data."
                emptyIcon={<MailOutlined />}
                server={{
                    page,
                    pageSize: PAGE_SIZE,
                    total: rows.length,
                    sortModel,
                    onChange: (current: number) => setPage(current),
                    onSortChange: (model: any[]) => {
                        setSortModel(model ?? []);
                        setPage(1);
                    },
                }}
            />
        </div>
    );
});

export default AccountDetail
